/**
 * Standard report codes for account mapping
 * Organized hierarchically: Category > Subcategory > Specific codes
 */

export interface ReportCode {
  code: string;
  name: string;
  category: "ASS" | "EQU" | "EXP" | "LIA" | "REV";
}

export const reportCodes: ReportCode[] = [
  // ASSETS
  { code: "ASS", name: "Assets", category: "ASS" },
  { code: "ASS.CUR", name: "Current assets", category: "ASS" },
  { code: "ASS.CUR.BAN", name: "Bank and cash/(bank overdraft)", category: "ASS" },
  { code: "ASS.CUR.CON", name: "Construction contracts", category: "ASS" },
  { code: "ASS.CUR.DIR", name: "Loans to directors/partners/trustees (current)", category: "ASS" },
  { code: "ASS.CUR.INV", name: "Investments (current)", category: "ASS" },
  { code: "ASS.CUR.INY", name: "Inventories", category: "ASS" },
  { code: "ASS.CUR.INY.WIP", name: "Work in Progress", category: "ASS" },
  { code: "ASS.CUR.REC", name: "Trade and other receivables", category: "ASS" },
  { code: "ASS.CUR.REC.PRE", name: "Prepayments", category: "ASS" },
  { code: "ASS.CUR.REC.TRA", name: "Trade receivables", category: "ASS" },
  { code: "ASS.CUR.REL", name: "Related party receivables/(payables) - current", category: "ASS" },
  { code: "ASS.NCA", name: "Non current assets", category: "ASS" },
  { code: "ASS.NCA.BUS", name: "Investments in businesses", category: "ASS" },
  { code: "ASS.NCA.BUS.ASS", name: "Investments in associates", category: "ASS" },
  { code: "ASS.NCA.BUS.SUB", name: "Investments in subsidiaries", category: "ASS" },
  { code: "ASS.NCA.DIR", name: "Loans to directors/partners/trustees (non current)", category: "ASS" },
  { code: "ASS.NCA.FIX", name: "Fixed assets", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA", name: "Leased fixed assets", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.ACC", name: "Accumulated depreciation - fixed assets leased", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.FUR", name: "Furniture and fittings leased", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.FUR.ACC", name: "Accumulated depreciation - furniture and fittings leased", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.PLA", name: "Plant and machinery leased", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.PLA.ACC", name: "Accumulated depreciation - plant and machinery leased", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.VEH", name: "Vehicles leased", category: "ASS" },
  { code: "ASS.NCA.FIX.LEA.VEH.ACC", name: "Accumulated depreciation - vehicles leased", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN", name: "Owned fixed assets", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.ACC", name: "Accumulated depreciation - fixed assets owned", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.BUC", name: "Buildings at cost", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.BUC.ACC", name: "Accumulated depreciation - buildings", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.BUR", name: "Buildings revaluation", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.FUR", name: "Furniture and fittings owned", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.FUR.ACC", name: "Accumulated depreciation - furniture and fittings owned", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.LAC", name: "Land at cost", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.LAR", name: "Land revaluation", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.PLA", name: "Plant and machinery owned", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.PLA.ACC", name: "Accumulated depreciation - plant and machinery owned", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.REV", name: "Fixed assets owned - revaluation", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.VEH", name: "Vehicles owned", category: "ASS" },
  { code: "ASS.NCA.FIX.OWN.VEH.ACC", name: "Accumulated depreciation - vehicles owned", category: "ASS" },
  { code: "ASS.NCA.INT", name: "Intangibles", category: "ASS" },
  { code: "ASS.NCA.INT.AMO", name: "Amortisation of Intangibles", category: "ASS" },
  { code: "ASS.NCA.INT.GOO", name: "Goodwill", category: "ASS" },
  { code: "ASS.NCA.INT.GOO.AMO", name: "Goodwill amortisation", category: "ASS" },
  { code: "ASS.NCA.INT.PAT", name: "Patents", category: "ASS" },
  { code: "ASS.NCA.INT.PAT.AMO", name: "Patents amortisation", category: "ASS" },
  { code: "ASS.NCA.INT.TRA", name: "Trademarks", category: "ASS" },
  { code: "ASS.NCA.INT.TRA.AMO", name: "Trademarks amortisation", category: "ASS" },
  { code: "ASS.NCA.INV", name: "Investments (non current)", category: "ASS" },
  { code: "ASS.NCA.INV.PRE", name: "Preference shares", category: "ASS" },
  { code: "ASS.NCA.INV.SHA", name: "Shares and debentures", category: "ASS" },
  { code: "ASS.NCA.INV.TER", name: "Term deposits", category: "ASS" },
  { code: "ASS.NCA.PRO", name: "Investment properties", category: "ASS" },
  { code: "ASS.NCA.REL", name: "Related party receivables/(payables) - non current", category: "ASS" },

  // EQUITY
  { code: "EQU", name: "Equity/Members funds", category: "EQU" },
  { code: "EQU.GIF", name: "Gifting", category: "EQU" },
  { code: "EQU.OWN.1", name: "Owners/partners funds introduced 1", category: "EQU" },
  { code: "EQU.OWN.2", name: "Owners/partners funds introduced 2", category: "EQU" },
  { code: "EQU.OWN.3", name: "Owners/partners funds introduced 3", category: "EQU" },
  { code: "EQU.OWN.4", name: "Owners/partners funds introduced 4", category: "EQU" },
  { code: "EQU.OWN.5", name: "Owners/partners funds introduced 5", category: "EQU" },
  { code: "EQU.OWN.DRA.1", name: "Owners/partners drawings 1", category: "EQU" },
  { code: "EQU.OWN.DRA.2", name: "Owners/partners drawings 2", category: "EQU" },
  { code: "EQU.OWN.DRA.3", name: "Owners/partners drawings 3", category: "EQU" },
  { code: "EQU.OWN.DRA.4", name: "Owners/partners drawings 4", category: "EQU" },
  { code: "EQU.OWN.DRA.5", name: "Owners/partners drawings 5", category: "EQU" },
  { code: "EQU.OWN", name: "Owners/partners funds introduced", category: "EQU" },
  { code: "EQU.OWN.DRA", name: "Owners/partners drawings", category: "EQU" },
  { code: "EQU.PRE", name: "Preference shares", category: "EQU" },
  { code: "EQU.RES", name: "Reserves", category: "EQU" },
  { code: "EQU.RES.FOR", name: "Foreign currency translation reserve", category: "EQU" },
  { code: "EQU.RES.LRR", name: "Livestock revaluation reserve", category: "EQU" },
  { code: "EQU.RES.REV", name: "Revaluation reserves", category: "EQU" },
  { code: "EQU.RET", name: "Retained earnings/Accumulated funds", category: "EQU" },
  { code: "EQU.RET.CUR", name: "Current year earnings", category: "EQU" },
  { code: "EQU.RET.DIV", name: "Dividends paid", category: "EQU" },
  { code: "EQU.RET.DIV.ORD", name: "Dividends paid on ordinary shares", category: "EQU" },
  { code: "EQU.RET.DIV.PRE", name: "Dividends paid on preference shares", category: "EQU" },
  { code: "EQU.SHA", name: "Capital", category: "EQU" },

  // EXPENSES
  { code: "EXP", name: "Expense", category: "EXP" },
  { code: "EXP.AMO", name: "Amortisation of intangibles", category: "EXP" },
  { code: "EXP.AMO.GOO", name: "Amortisation of goodwill", category: "EXP" },
  { code: "EXP.AUD", name: "Audit fees", category: "EXP" },
  { code: "EXP.BAD", name: "Bad debts", category: "EXP" },
  { code: "EXP.BAD.PDD", name: "Allowance for doubtful debts", category: "EXP" },
  { code: "EXP.CON", name: "Contractor payments", category: "EXP" },
  { code: "EXP.COS", name: "Cost of goods sold", category: "EXP" },
  { code: "EXP.COS.CLO", name: "Closing inventory", category: "EXP" },
  { code: "EXP.COS.CLO.FAR", name: "Closing livestock", category: "EXP" },
  { code: "EXP.COS.CLO.FAR.BEE", name: "Closing livestock - beef cattle", category: "EXP" },
  { code: "EXP.COS.CLO.FAR.DAI", name: "Closing livestock - dairy cattle", category: "EXP" },
  { code: "EXP.COS.CLO.FAR.SHE", name: "Closing livestock - sheep", category: "EXP" },
  { code: "EXP.COS.CLO.FAR.DER", name: "Closing livestock - deer", category: "EXP" },
  { code: "EXP.COS.CLO.FAR.GOA", name: "Closing livestock - goats", category: "EXP" },
  { code: "EXP.COS.CLO.FAR.PIG", name: "Closing livestock - pigs", category: "EXP" },
  { code: "EXP.COS.OPE", name: "Opening inventory", category: "EXP" },
  { code: "EXP.COS.OPE.FAR", name: "Opening livestock", category: "EXP" },
  { code: "EXP.COS.OPE.FAR.BEE", name: "Opening livestock - beef cattle", category: "EXP" },
  { code: "EXP.COS.OPE.FAR.DAI", name: "Opening livestock - dairy cattle", category: "EXP" },
  { code: "EXP.COS.OPE.FAR.SHE", name: "Opening livestock - sheep", category: "EXP" },
  { code: "EXP.COS.OPE.FAR.DER", name: "Opening livestock - deer", category: "EXP" },
  { code: "EXP.COS.OPE.FAR.GOA", name: "Opening livestock - goats", category: "EXP" },
  { code: "EXP.COS.OPE.FAR.PIG", name: "Opening livestock - pigs", category: "EXP" },
  { code: "EXP.COS.PUR", name: "Purchases", category: "EXP" },
  { code: "EXP.COS.PUR.FAR", name: "Direct costs - farming", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.MIL", name: "Direct costs - dairy produce / milk", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.DAI", name: "Direct costs - dairy cattle", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.BEE", name: "Direct Costs - beef cattle", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.SHE", name: "Direct costs - sheep", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.GOA", name: "Direct costs - goats", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.DER", name: "Direct costs - deer", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.PIG", name: "Direct costs - pigs", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.WOO", name: "Direct costs - wool", category: "EXP" },
  { code: "EXP.COS.PUR.FAR.HOR", name: "Direct costs - horticultural produce", category: "EXP" },
  { code: "EXP.COS.WPO", name: "Opening WIP", category: "EXP" },
  { code: "EXP.COS.WPC", name: "Closing WIP", category: "EXP" },
  { code: "EXP.DEP", name: "Depreciation", category: "EXP" },
  { code: "EXP.DIR", name: "Directors/trustees fees", category: "EXP" },
  { code: "EXP.DIS", name: "Distributions to Beneficiaries", category: "EXP" },
  { code: "EXP.ENT", name: "Entertainment", category: "EXP" },
  { code: "EXP.ENT.NON", name: "Non deductible entertainment", category: "EXP" },
  { code: "EXP.EXT", name: "Non recurring items", category: "EXP" },
  { code: "EXP.FAR", name: "Farm expenses", category: "EXP" },
  { code: "EXP.FAR.ANI", name: "Animal health", category: "EXP" },
  { code: "EXP.FAR.FEE", name: "Feed", category: "EXP" },
  { code: "EXP.FAR.PAS", name: "Pasture", category: "EXP" },
  { code: "EXP.FBT", name: "Fringe benefit tax", category: "EXP" },
  { code: "EXP.FOR", name: "Foreign currency gains and losses", category: "EXP" },
  { code: "EXP.FOR.RGL", name: "Realised foreign currency gains and losses", category: "EXP" },
  { code: "EXP.FOR.UGL", name: "Unrealised foreign currency gains and losses", category: "EXP" },
  { code: "EXP.FUN", name: "Expenses relating to public fundraising", category: "EXP" },
  { code: "EXP.GRA", name: "Grants and Donations made", category: "EXP" },
  { code: "EXP.IMP", name: "Impairment loss", category: "EXP" },
  { code: "EXP.INS", name: "Insurance", category: "EXP" },
  { code: "EXP.INT", name: "Interest and finance charges", category: "EXP" },
  { code: "EXP.LEG", name: "Professional and consulting fees", category: "EXP" },
  { code: "EXP.LEG.NON", name: "Non deductible professional and consulting fees", category: "EXP" },
  { code: "EXP.LOS", name: "Loss on disposal of property, plant and equipment", category: "EXP" },
  { code: "EXP.NON", name: "Other non deductible items", category: "EXP" },
  { code: "EXP.RAT", name: "Rates", category: "EXP" },
  { code: "EXP.REN", name: "Rental and lease payments", category: "EXP" },
  { code: "EXP.REN.OPE", name: "Operating lease payments", category: "EXP" },
  { code: "EXP.REP", name: "Repairs and maintenance", category: "EXP" },
  { code: "EXP.RES", name: "Research and development", category: "EXP" },
  { code: "EXP.TAX", name: "Income tax expense", category: "EXP" },
  { code: "EXP.TRA", name: "Travel and accommodation", category: "EXP" },
  { code: "EXP.TRA.INT", name: "International travel and accommodation", category: "EXP" },
  { code: "EXP.TRA.NAT", name: "National travel and accommodation", category: "EXP" },
  { code: "EXP.VEH", name: "Vehicle expenses", category: "EXP" },
  { code: "EXP.WAG", name: "Wages and salaries", category: "EXP" },
  { code: "EXP.WAG.HOL", name: "Holiday pay accrual", category: "EXP" },
  { code: "EXP.WAG.SHA", name: "Shareholder Salaries", category: "EXP" },
  { code: "EXP.UDE", name: "User Defined Expenses", category: "EXP" },
  { code: "EXP.UDE.1", name: "User Defined Expense 1", category: "EXP" },
  { code: "EXP.UDE.2", name: "User Defined Expense 2", category: "EXP" },
  { code: "EXP.UDE.3", name: "User Defined Expense 3", category: "EXP" },
  { code: "EXP.UDE.4", name: "User Defined Expense 4", category: "EXP" },
  { code: "EXP.UDE.5", name: "User Defined Expense 5", category: "EXP" },

  // LIABILITIES
  { code: "LIA", name: "Liabilities", category: "LIA" },
  { code: "LIA.CUR", name: "Current Liabilities", category: "LIA" },
  { code: "LIA.CUR.DON", name: "Unused Donations and Grants with Conditions", category: "LIA" },
  { code: "LIA.CUR.FIN", name: "Finance lease loans (current)", category: "LIA" },
  { code: "LIA.CUR.LOA", name: "Loans", category: "LIA" },
  { code: "LIA.CUR.LOA.DIR", name: "Loans from directors/partners/trustees (current)", category: "LIA" },
  { code: "LIA.CUR.LOA.SEC", name: "Secured loans (current)", category: "LIA" },
  { code: "LIA.CUR.LOA.UNS", name: "Unsecured loans (current)", category: "LIA" },
  { code: "LIA.CUR.PAY", name: "Trade and other payables", category: "LIA" },
  { code: "LIA.CUR.PAY.DIV", name: "Dividends payable", category: "LIA" },
  { code: "LIA.CUR.PAY.DIV.ORD", name: "Dividends payable on ordinary shares", category: "LIA" },
  { code: "LIA.CUR.PAY.DIV.PRE", name: "Dividends payable on preference shares", category: "LIA" },
  { code: "LIA.CUR.PAY.EMP", name: "Employee entitlements (wages, annual leave, etc)", category: "LIA" },
  { code: "LIA.CUR.PAY.FBT", name: "Fringe benefit tax", category: "LIA" },
  { code: "LIA.CUR.PAY.PAY", name: "PAYE", category: "LIA" },
  { code: "LIA.CUR.PAY.TRA", name: "Trade payables", category: "LIA" },
  { code: "LIA.CUR.PAY.UNE", name: "Unearned income", category: "LIA" },
  { code: "LIA.CUR.PRO", name: "Provisions", category: "LIA" },
  { code: "LIA.CUR.PRO.ONE", name: "Onerous contracts", category: "LIA" },
  { code: "LIA.CUR.PRO.WAR", name: "Warranties", category: "LIA" },
  { code: "LIA.CUR.REL", name: "Related party payables (current)", category: "LIA" },
  { code: "LIA.NCL.REL", name: "Related party payables (non current)", category: "LIA" },
  { code: "LIA.CUR.TAX", name: "Tax", category: "LIA" },
  { code: "LIA.CUR.TAX.DEF", name: "Deferred tax", category: "LIA" },
  { code: "LIA.CUR.TAX.GST", name: "Goods and services tax", category: "LIA" },
  { code: "LIA.CUR.TAX.INC", name: "Income tax", category: "LIA" },
  { code: "LIA.CUR.TAX.INC.PAI", name: "Income tax paid", category: "LIA" },
  { code: "LIA.CUR.TAX.INC.REF", name: "Income tax refunded", category: "LIA" },
  { code: "LIA.CUR.TAX.INC.TAX", name: "Tax credits", category: "LIA" },
  { code: "LIA.CUR.TAX.INC.TAX.IMP", name: "Imputation credits on dividends received", category: "LIA" },
  { code: "LIA.CUR.TAX.INC.TAX.OTH", name: "Other tax credits", category: "LIA" },
  { code: "LIA.CUR.TAX.INC.TAX.RWT", name: "Resident withholding tax paid", category: "LIA" },
  { code: "LIA.NCL", name: "Non current liabilities", category: "LIA" },
  { code: "LIA.NCL.ADV.1", name: "Beneficiary/shareholder advances 1", category: "LIA" },
  { code: "LIA.NCL.ADV.2", name: "Beneficiary/shareholder advances 2", category: "LIA" },
  { code: "LIA.NCL.ADV.3", name: "Beneficiary/shareholder advances 3", category: "LIA" },
  { code: "LIA.NCL.ADV.4", name: "Beneficiary/shareholder advances 4", category: "LIA" },
  { code: "LIA.NCL.ADV.5", name: "Beneficiary/shareholder advances 5", category: "LIA" },
  { code: "LIA.NCL.ADV.DRA.1", name: "Beneficiary/shareholder drawings 1", category: "LIA" },
  { code: "LIA.NCL.ADV.DRA.2", name: "Beneficiary/shareholder drawings 2", category: "LIA" },
  { code: "LIA.NCL.ADV.DRA.3", name: "Beneficiary/shareholder drawings 3", category: "LIA" },
  { code: "LIA.NCL.ADV.DRA.4", name: "Beneficiary/shareholder drawings 4", category: "LIA" },
  { code: "LIA.NCL.ADV.DRA.5", name: "Beneficiary/shareholder drawings 5", category: "LIA" },
  { code: "LIA.NCL.ADV", name: "Beneficiary/shareholder advances", category: "LIA" },
  { code: "LIA.NCL.ADV.DRA", name: "Beneficiary/shareholder drawings", category: "LIA" },
  { code: "LIA.NCL.FIN", name: "Finance lease loans (non current)", category: "LIA" },
  { code: "LIA.NCL.LOA", name: "Loans", category: "LIA" },
  { code: "LIA.NCL.LOA.DIR", name: "Loans from directors/partners/trustees (non current)", category: "LIA" },
  { code: "LIA.NCL.LOA.SEC", name: "Secured loans (non current)", category: "LIA" },
  { code: "LIA.NCL.LOA.UNS", name: "Unsecured loans (non current)", category: "LIA" },

  // REVENUE
  { code: "REV", name: "Revenue", category: "REV" },
  { code: "REV.INV", name: "Investment revenue", category: "REV" },
  { code: "REV.INV.ASS", name: "Share of surplus of associates, partnerships and joint ventures", category: "REV" },
  { code: "REV.INV.DIV", name: "Dividends", category: "REV" },
  { code: "REV.INV.INT", name: "Interest", category: "REV" },
  { code: "REV.INV.REN", name: "Rents", category: "REV" },
  { code: "REV.NON", name: "Non-Taxable Income", category: "REV" },
  { code: "REV.OTH", name: "Other revenue", category: "REV" },
  { code: "REV.OTH.GAI", name: "Gain on disposal of property, plant and equipment", category: "REV" },
  { code: "REV.OTH.GAI.CAP", name: "Capital gain on disposal of property, plant and equipment", category: "REV" },
  { code: "REV.OTH.GRA", name: "Grants", category: "REV" },
  { code: "REV.OTH.SUB", name: "Gain on disposal of subsidiary", category: "REV" },
  { code: "REV.TRA", name: "Trading revenue", category: "REV" },
  { code: "REV.TRA.CON", name: "Construction contracts", category: "REV" },
  { code: "REV.TRA.MAN", name: "Manufactured goods and services", category: "REV" },
  { code: "REV.TRA.OPE", name: "Operating leases", category: "REV" },
  { code: "REV.TRA.RET", name: "Retail goods and services", category: "REV" },
  { code: "REV.TRA.FAR", name: "Farm income", category: "REV" },
  { code: "REV.TRA.FAR.HOR", name: "Sale of horticultural produce", category: "REV" },
  { code: "REV.TRA.FAR.MIL", name: "Sale of dairy produce / milk", category: "REV" },
  { code: "REV.TRA.FAR.DAI", name: "Sale of dairy cattle", category: "REV" },
  { code: "REV.TRA.FAR.BEE", name: "Sale of beef cattle", category: "REV" },
  { code: "REV.TRA.FAR.SHE", name: "Sale of sheep", category: "REV" },
  { code: "REV.TRA.FAR.GOA", name: "Sale of goats", category: "REV" },
  { code: "REV.TRA.FAR.DER", name: "Sale of deer", category: "REV" },
  { code: "REV.TRA.FAR.PIG", name: "Sale of pigs", category: "REV" },
  { code: "REV.TRA.FAR.WOO", name: "Sale of wool", category: "REV" },
  { code: "REV.DON", name: "Donations received", category: "REV" },
  { code: "REV.FEE", name: "Fees received", category: "REV" },
  { code: "REV.FUN", name: "Fundraising Revenue", category: "REV" },
  { code: "REV.SUB", name: "Subscription from Members", category: "REV" },
];

// Category display names
export const categoryNames: Record<string, string> = {
  ASS: "Assets",
  EQU: "Equity",
  EXP: "Expenses",
  LIA: "Liabilities",
  REV: "Revenue",
};

// Create a lookup map for fast access
export const reportCodeMap = new Map(reportCodes.map((rc) => [rc.code, rc]));

/**
 * Get display text for a report code
 */
export function getReportCodeDisplay(code: string | null): string {
  if (!code) return "";
  const found = reportCodeMap.get(code);
  return found ? `${code} - ${found.name}` : code;
}
